import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, ArrowRight, ArrowLeft, Fingerprint, Brain } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import { useWebAuthn } from '@/hooks/useWebAuthn';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { markPwaHighlightAfterLogin } from '@/lib/pwa-install';
import focoLogo from '@/assets/foco-ai-logo.png';
import {
  LEGAL_SUITE_VERSION,
  LEGAL_SUITE_LAST_UPDATED_LABEL,
} from '@/constants/legalSuite';

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

const strongPasswordSchema = z.string()
  .min(8, 'La contraseña debe tener al menos 8 caracteres')
  .refine((val) => /[A-Z]/.test(val), 'Debe incluir al menos una letra mayúscula')
  .refine((val) => /[a-z]/.test(val), 'Debe incluir al menos una letra minúscula')
  .refine((val) => /[0-9]/.test(val), 'Debe incluir al menos un número');

const signupSchema = z.object({
  email: z.string().email('Email inválido'),
  password: strongPasswordSchema,
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword'],
});

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(true);
  const [showMobileLanding, setShowMobileLanding] = useState(true);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const { user, signIn, signUp } = useAuth();
  const { 
    isSupported: webAuthnSupported, 
    hasRegisteredBiometric, 
    authenticateWithBiometric,
    checkBiometricAvailability,
    isLoading: biometricLoading 
  } = useWebAuthn();
  const navigate = useNavigate();

  const savedEmail = localStorage.getItem('foco_remembered_email') || '';

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const checkBiometric = async () => {
      if (webAuthnSupported) {
        const available = await checkBiometricAvailability();
        setBiometricAvailable(available && hasRegisteredBiometric());
      }
    };
    checkBiometric();
  }, [webAuthnSupported, checkBiometricAvailability, hasRegisteredBiometric]);

  const handleBiometricLogin = async () => {
    setIsLoading(true);
    try {
      const result = await authenticateWithBiometric();
      if (result) {
        const { data: { session } } = await (
          await import('@/integrations/supabase/client')
        ).supabase.auth.getSession();

        if (session) {
          toast({ title: '¡Bienvenido!' });
          markPwaHighlightAfterLogin();
          navigate('/dashboard');
        } else {
          loginForm.setValue('email', result.email);
          toast({
            title: 'Identidad verificada',
            description: 'Ingresa tu contraseña para continuar',
          });
        }
      } else {
        toast({
          title: 'Error de autenticación',
          description: 'No se pudo verificar tu identidad biométrica',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('Biometric login flow failed:', err);
      toast({
        title: 'Error',
        description: 'No se pudo completar el inicio de sesión. Intenta de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: savedEmail, password: '' },
  });

  const signupForm = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: { name: '', email: '', password: '', confirmPassword: '' },
  });

  const handleLogin = async (values: z.infer<typeof loginSchema>) => {
    setIsLoading(true);
    if (rememberEmail) {
      localStorage.setItem('foco_remembered_email', values.email);
    } else {
      localStorage.removeItem('foco_remembered_email');
    }
    const { error } = await signIn(values.email, values.password);
    setIsLoading(false);
    if (error) {
      toast({
        title: 'Error al iniciar sesión',
        description: error.message === 'Invalid login credentials' 
          ? 'Credenciales inválidas. Verifica tu email y contraseña.'
          : error.message,
        variant: 'destructive',
      });
    } else {
      toast({ title: '¡Bienvenido!' });
      markPwaHighlightAfterLogin();
      navigate('/dashboard');
    }
  };

  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleSignup = async (values: z.infer<typeof signupSchema>) => {
    if (!acceptedTerms) {
      toast({
        title: 'Aceptación requerida',
        description:
          'Marca la casilla para aceptar términos, privacidad, cookies y el consentimiento de datos.',
        variant: 'destructive',
      });
      return;
    }
    setIsLoading(true);
    const { error, data } = await signUp(values.email, values.password, values.name);
    
    if (error) {
      setIsLoading(false);
      let errorTitle = 'Error al registrarse';
      let errorMessage = error.message;
      if (error.message.includes('already registered') || 
          error.message.includes('user_already_exists') ||
          error.message.includes('User already registered')) {
        errorTitle = 'Email ya registrado';
        errorMessage = 'Este email ya tiene una cuenta. Intenta iniciar sesión o recuperar tu contraseña.';
      } else if (error.message.includes('network') || error.message.includes('Load failed') || error.message.includes('fetch')) {
        errorTitle = 'Error de conexión';
        errorMessage = 'No se pudo conectar al servidor. Verifica tu conexión a internet e intenta de nuevo.';
      } else if (error.message.includes('rate limit') || error.message.includes('too many')) {
        errorTitle = 'Demasiados intentos';
        errorMessage = 'Has excedido el límite de intentos. Espera unos minutos antes de intentar de nuevo.';
      }
      toast({ title: errorTitle, description: errorMessage, variant: 'destructive' });
      return;
    }
    
    if (data?.user) {
      try {
        await supabase.from('consent_records').insert([
          { user_id: data.user.id, consent_type: 'terms_and_conditions', consent_version: LEGAL_SUITE_VERSION, accepted: true, user_agent: navigator.userAgent },
          { user_id: data.user.id, consent_type: 'privacy_policy', consent_version: LEGAL_SUITE_VERSION, accepted: true, user_agent: navigator.userAgent },
          { user_id: data.user.id, consent_type: 'data_processing', consent_version: LEGAL_SUITE_VERSION, accepted: true, user_agent: navigator.userAgent },
        ]);
      } catch (consentError) {
        console.error('Error saving consent:', consentError);
      }
      if (data.session) {
        setIsLoading(false);
        toast({ title: '¡Cuenta creada!', description: 'Bienvenido a FOCO.' });
        markPwaHighlightAfterLogin();
        navigate('/dashboard');
        return;
      }
    }

    setIsLoading(false);
    toast({
      title: '¡Cuenta creada!',
      description: 'Revisa tu correo y abre el enlace de confirmación para activar tu cuenta.',
    });
    signupForm.reset();
    setIsLogin(true);
  };

  const features = [
    'Dashboard inteligente',
    'FOCO Score con IA',
    'Análisis financiero',
    'Reportes PDF',
  ];

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col lg:flex-row">
      {/* Mobile Landing Screen */}
      <div className={`lg:hidden flex flex-col min-h-[100dvh] bg-[#1F2F4A] p-6 sm:p-8 relative overflow-hidden ${showMobileLanding ? 'flex' : 'hidden'}`}>
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        
        <div className="flex-1 flex flex-col justify-center items-center relative z-10 text-center space-y-6 safe-area-inset">
          <img 
            src={focoLogo} 
            alt="FOCO AI System" 
            className="h-20 sm:h-24 w-auto"
          />
          
          <div className="space-y-3 max-w-sm">
            <h1 className="text-white text-2xl sm:text-3xl font-bold leading-tight">
              Inteligencia financiera para decisiones inteligentes
            </h1>
            <p className="text-white/60 text-sm sm:text-base">
              Monitorea, analiza y optimiza tus finanzas personales con tecnología de IA avanzada.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 max-w-xs">
            {features.map((feature, i) => (
              <div key={i} className="flex items-center justify-center gap-2 text-white/70">
                <Brain className="h-3 w-3 sm:h-4 sm:w-4 text-[#2ECC71] flex-shrink-0" />
                <span className="text-xs sm:text-sm">{feature}</span>
              </div>
            ))}
          </div>

          <div className="pt-4 w-full max-w-xs space-y-3">
            <Button 
              onClick={() => { setIsLogin(true); setShowMobileLanding(false); }}
              className="w-full h-14 text-lg font-semibold touch-manipulation bg-gradient-to-r from-[#2E86C1] to-[#2ECC71] hover:from-[#2E86C1]/90 hover:to-[#2ECC71]/90 text-white border-0"
            >
              Comenzar
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="text-white/30 text-xs pt-2 text-center">
              © 2025 FOCO AI System
            </p>
          </div>
        </div>
      </div>

      {/* Left panel - Branding (Desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1F2F4A] p-12 flex-col justify-center items-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        
        <div className="space-y-8 relative z-10 text-center max-w-lg">
          <div className="flex flex-col items-center gap-8">
            <img 
              src={focoLogo} 
              alt="FOCO AI System" 
              className="h-24 w-auto"
            />
            <h1 className="text-white text-3xl font-bold leading-tight">
              Inteligencia financiera<br />para decisiones inteligentes
            </h1>
          </div>

          <p className="text-white/50 text-base max-w-md mx-auto">
            Monitorea, analiza y optimiza tus finanzas personales con tecnología de inteligencia artificial avanzada.
          </p>

          <div className="grid grid-cols-2 gap-4 pt-4">
            {features.map((feature, i) => (
              <div key={i} className="flex items-center justify-center gap-2 text-white/60">
                <Brain className="h-4 w-4 text-[#2ECC71]" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>

          <p className="text-white/20 text-sm pt-8">
            © 2025 FOCO AI System. Todos los derechos reservados.
          </p>
        </div>
      </div>

      {/* Right panel - Form */}
      <div className={`flex-1 flex items-center justify-center px-4 py-6 sm:px-8 sm:py-8 min-h-screen min-h-[100dvh] lg:min-h-0 overflow-y-auto safe-area-inset ${!showMobileLanding ? 'flex' : 'hidden lg:flex'}`}>
        <div className="w-full max-w-md space-y-5 sm:space-y-6 py-6 sm:py-8 lg:py-0">
          {/* Back button for mobile */}
          <Button
            variant="ghost"
            onClick={() => setShowMobileLanding(true)}
            className="lg:hidden -ml-2 mb-2 text-muted-foreground touch-manipulation"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>

          <div className="text-center lg:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold">
              {isLogin ? 'Iniciar sesión' : 'Crear cuenta'}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              {isLogin 
                ? 'Ingresa tus credenciales para continuar' 
                : 'Completa el formulario para comenzar'}
            </p>
          </div>

          {isLogin ? (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="login-email" className="text-sm font-medium leading-none">Email</label>
                <Input 
                  id="login-email" type="email" inputMode="email" autoComplete="username email"
                  autoCapitalize="none" autoCorrect="off" enterKeyHint="next"
                  placeholder="tu@email.com" className="h-12 text-base"
                  {...loginForm.register('email')}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-sm font-medium text-destructive">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="login-password" className="text-sm font-medium leading-none">Contraseña</label>
                <div className="relative">
                  <Input
                    id="login-password" type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password" enterKeyHint="go"
                    placeholder="••••••••" className="h-12 text-base pr-12"
                    {...loginForm.register('password')}
                  />
                  <Button type="button" variant="ghost" size="icon"
                    className="absolute right-0 top-0 h-full w-12 px-3 touch-manipulation"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5 text-muted-foreground" /> : <Eye className="h-5 w-5 text-muted-foreground" />}
                  </Button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-sm font-medium text-destructive">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <input type="checkbox" id="remember-email" checked={rememberEmail}
                  onChange={(e) => setRememberEmail(e.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-ring touch-manipulation"
                />
                <label htmlFor="remember-email" className="text-sm text-muted-foreground cursor-pointer touch-manipulation">
                  Recordar mi email
                </label>
              </div>

              <Button type="submit" className="w-full h-12 text-base touch-manipulation" disabled={isLoading || biometricLoading}>
                {isLoading ? 'Cargando...' : 'Iniciar sesión'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              {biometricAvailable && (
                <Button type="button" variant="outline" className="w-full h-12 text-base touch-manipulation"
                  onClick={handleBiometricLogin} disabled={isLoading || biometricLoading}
                >
                  <Fingerprint className="mr-2 h-5 w-5" />
                  {biometricLoading ? 'Verificando...' : 'Usar Face ID / Huella'}
                </Button>
              )}

              <div className="text-center">
                <Link to="/forgot-password">
                  <Button type="button" variant="link" className="text-muted-foreground text-sm touch-manipulation">
                    ¿Olvidaste tu contraseña?
                  </Button>
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="signup-name" className="text-sm font-medium leading-none">Nombre completo</label>
                <Input id="signup-name" type="text" autoComplete="name" autoCapitalize="words"
                  placeholder="Tu nombre" className="h-12 text-base" {...signupForm.register('name')} />
                {signupForm.formState.errors.name && (
                  <p className="text-sm font-medium text-destructive">{signupForm.formState.errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="signup-email" className="text-sm font-medium leading-none">Email</label>
                <Input id="signup-email" type="email" inputMode="email" autoComplete="email"
                  autoCapitalize="none" autoCorrect="off" placeholder="tu@email.com" className="h-12 text-base"
                  {...signupForm.register('email')} />
                {signupForm.formState.errors.email && (
                  <p className="text-sm font-medium text-destructive">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="signup-password" className="text-sm font-medium leading-none">Contraseña</label>
                <div className="relative">
                  <Input id="signup-password" type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password" placeholder="••••••••" className="h-12 text-base pr-12"
                    {...signupForm.register('password')} />
                  <Button type="button" variant="ghost" size="icon"
                    className="absolute right-0 top-0 h-full w-12 px-3 touch-manipulation"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5 text-muted-foreground" /> : <Eye className="h-5 w-5 text-muted-foreground" />}
                  </Button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-sm font-medium text-destructive">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="signup-confirm" className="text-sm font-medium leading-none">Confirmar contraseña</label>
                <Input id="signup-confirm" type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password" placeholder="••••••••" className="h-12 text-base"
                  {...signupForm.register('confirmPassword')} />
                {signupForm.formState.errors.confirmPassword && (
                  <p className="text-sm font-medium text-destructive">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex items-start gap-2">
                <Checkbox id="accept-terms" checked={acceptedTerms}
                  onCheckedChange={(v) => setAcceptedTerms(v === true)} className="mt-0.5" />
                <label htmlFor="accept-terms" className="text-xs text-muted-foreground leading-relaxed">
                  Declaro haber leído y acepto los{' '}
                  <Link to="/terms-and-conditions" className="text-accent underline">Términos y Condiciones</Link>, la{' '}
                  <Link to="/privacy-policy" className="text-accent underline">Política de Privacidad</Link>, la{' '}
                  <Link to="/cookies-policy" className="text-accent underline">Política de Cookies</Link> y el tratamiento
                  de mis datos personales según la{' '}
                  <Link to="/consent-information" className="text-accent underline">Información sobre consentimiento</Link>
                  , conforme al consentimiento unificado{' '}
                  <span className="font-medium text-foreground">versión {LEGAL_SUITE_VERSION}</span> (vigente desde{' '}
                  {LEGAL_SUITE_LAST_UPDATED_LABEL}) para México, Centroamérica y Costa Rica.
                </label>
              </div>

              <Button type="submit" className="w-full h-12 text-base touch-manipulation" disabled={isLoading || !acceptedTerms}>
                {isLoading ? 'Cargando...' : 'Crear cuenta'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>
          )}

          <div className="text-center pb-6 sm:pb-4 lg:pb-0">
            <Button variant="link" onClick={() => setIsLogin(!isLogin)}
              className="text-muted-foreground h-12 touch-manipulation"
            >
              {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
